import { useQuery } from '@tanstack/react-query'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDeepCompareEffect } from 'react-use'
import { Nullish } from '~/@types/generic'
import { Variant } from '~/@types/product'
import { getBackgroundUrl } from '~/lib/akairos/get-background-image-url'
import { getSaucerImgUrl } from '~/lib/akairos/get-saucer-image-url'
import { PRODUCT_TYPES } from '~/lib/constants'

import { AddToCartProps } from '~/components/AddToCart'
import { ImageProps } from '~/components/UI/Image'

import { usePackProductsProvider } from '~/providers/PackProductsProvider'
import { useShopifyProductContext } from '~/providers/ShopifyProductProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import { useGetAddToCartProductProps } from '~/hooks/useGetAddToCartProductProps'
import useGetPreviewImage from '~/hooks/useGetPreviewImage'
import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'
import useLocale from '~/hooks/useLocale'
import usePostPreview from '~/hooks/usePostPreview'

import deepEqualCompareVariants from '~/utils/deep-equal-compare-variants'
import { fetchAndSerializeTabProducts } from '~/utils/fetch-and-serialize-tab-products'
import { getAllVariantsFromShopifyProduct } from '~/utils/get-all-variants-from-shopify-product'

import { SerializedProduct } from '~/data/product-card-data'
import { CustomizationOptions } from '~/data/product-card-data/types'

const Context = createContext<ProductHeaderProviderContext>(null)

export interface LoveNoteProps {
  note?: string
  signature?: string
}

export type PreviewSource = Record<'html' | 'src' | 'type', string>

export interface CustomizerVariant extends Variant {
  variantImage?: ImageProps
}

export type UserSelection = Record<any, any>[]

export type LabelCustomization = {
  title: string
  subtitle: string
  html?: string
  computeSubtitleFirst?: boolean
}

export function getAddToCartLabelCustomizationProps({ labelCustomization }) {
  return labelCustomization?.variant?.id
    ? {
        id: labelCustomization.variant?.id,
      }
    : null
}

export interface ProductHeaderProviderContext {
  addToCartDefaultProps: AddToCartProps
  customizationProducts?: SerializedProduct[]
  isDisabledEngraving?: boolean
  isDisabledLabelCustomization?: boolean
  setAtelierDate: Dispatch<SetStateAction<string>>
  setCurrentUpsaleId: Dispatch<SetStateAction<string>>
  currentUpsaleId: string
  loveNote: [LoveNoteProps, Dispatch<SetStateAction<LoveNoteProps>>]
  mainCustomizationProduct: SerializedProduct
  previewSources: PreviewSource[][]
  customization?: CustomizationOptions
  setLabelCustomization: Dispatch<SetStateAction<LabelCustomization>>
  labelCustomization: LabelCustomization
  currentLabelInputsContent: LabelCustomization
  isLoadingPreviewImages: boolean
  isLoadingLabelPreview: boolean
  isLoadingLabelPreviewData: boolean
  shopifyId: string
  isBundle?: boolean
  isMultiBundle?: boolean
  shopifyProduct?: SerializedProduct
  isLoadingTabProducts?: boolean
  customizationImageState: [
    Nullish<string[]>,
    Dispatch<SetStateAction<Nullish<string[]>>>,
  ]
}

type SerializedProductKeys =
  | 'productName'
  | 'customizationSubtitle'
  | 'shopifyId'
  | 'slug'
  | 'availableForSale'
  | 'defaultVariant'
  | 'hasUpsales'
  | 'productType'
  | 'variants'
  | 'defaultVariant'

export interface ProductHeaderProviderProps
  extends Pick<SerializedProduct, SerializedProductKeys>,
    Pick<SerializedProduct, 'media'> {
  children?: ReactNode | ReactNode[]
  categories?: string[]
  meanings?: string[]
  isGiftCard?: boolean
  hasDefaultLabelCustomization?: boolean
  excludeMetalPot?: boolean
  excludeCeramicPot?: boolean
  packTabsIds?: string[][]
  shopifyProduct?: SerializedProduct
  customizationProducts?: SerializedProduct[]
  tags?: string[]
  customization?: CustomizationOptions
  isMultiBundle?: boolean
}

export function useProductHeaderProvider() {
  return useContext(Context)
}

function ProductHeaderProvider({
  children,
  isGiftCard,
  customization,
  slug,
  shopifyProduct,
  packTabsIds,
  customizationProducts,
  isMultiBundle,
}: ProductHeaderProviderProps) {
  const { data, setData } = usePackProductsProvider()
  const {
    isLoading: isLoadingShopifyProduct,
    isSuccess: isSuccessShopifyProduct,
    shopifyData: updatedShopifyProduct,
  } = useShopifyProductContext()

  // Getting the useQuery state
  const isFullyDoneLoadingProduct =
    !isLoadingShopifyProduct && isSuccessShopifyProduct
  const allVariants = getAllVariantsFromShopifyProduct(shopifyProduct)
  const updatedVariants = getAllVariantsFromShopifyProduct(
    updatedShopifyProduct,
  )

  // Getting the equality of the set of variants
  const isVariantsEqual = deepEqualCompareVariants(updatedVariants, allVariants)

  const upToDateShopifyProduct = useMemo(() => {
    return isVariantsEqual
      ? shopifyProduct
      : { ...shopifyProduct, ...updatedShopifyProduct }
  }, [isFullyDoneLoadingProduct])

  // make sure we update the serialized data
  useDeepCompareEffect(() => {
    setData((prev) => {
      const newData = prev.map((tab, i) =>
        tab.map((product, j) => ({
          ...product,
          ...upToDateShopifyProduct?.packProducts?.[i]?.[j],
        })),
      )
      return newData
    })
  }, [upToDateShopifyProduct])

  const shopifyId = shopifyProduct?.shopifyId

  const { userSelection, setUserSelection } = useUserSelectionProvider()
  const locale = useLocale()
  const [userLabels, setUserLabels] = useState(
    data.map((tab) => tab?.[0]?.defaultLabel ?? null),
  )

  // Store the customization images for each product of a bundle
  const customizationImageState = useState(data.map(() => null))

  const {
    activeTabIndex,
    product,
    activeTab,
    activeCustomizationVariant,
    currentVariant,
    customizerVariants,
    customizerProducts,
    isDisabledEngraving,
    labelCustomizationVariant,
    isDisabledLabelCustomization,
  } = useGetStateInformationsFromUserSelection()

  const labelCustomizationProduct = product?.labelCustomizationProducts?.find(
    (product) => product.shopifyId === activeTab.labelCustomization,
  )

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [activeTabIndex])

  const disablePreview = product?.disablePreview

  const requiredCustomizationProduct =
    product?.requiredCustomizationProduct ?? null

  const isBundle = Boolean(
    product?.labelCustomizationProducts || customizationProducts,
  )

  // Some bundle products need to have a default label value that is set if the user doesnt provide any customization
  const defaultLabel = product?.defaultLabel

  const [atelierDate, setAtelierDate] = useState(null)

  /* Finding the product in the customizerProducts array that has the same productType as the
  requiredCustomizationProduct.
  This allows us to correctly set our bundle customizable products
   */
  const mainCustomizationProduct = customizerProducts?.find(
    (product) =>
      product.productType === requiredCustomizationProduct?.productType,
  )

  const mainCustomizationActiveVariant =
    mainCustomizationProduct?.variants?.find(
      ({ id }) => activeTab[mainCustomizationProduct?.productType] === id,
    ) ?? null

  // Find saucer if needed
  const customizerSaucer = customizerProducts?.find(
    (product) => product.productType === PRODUCT_TYPES.SAUCER,
  )

  const customizerSaucerActiveVariant =
    customizerSaucer?.variants?.find(({ id }) => activeTab?.saucer === id) ??
    null

  const loveNote = useState<LoveNoteProps>({
    note: '',
    signature: '',
  })

  const [labelCustomization, setLabelCustomization] = useState(
    defaultLabel
      ? {
          title: defaultLabel?.rawTitle,
          subtitle: defaultLabel?.rawSubTitle,
          computeSubtitleFirst: true,
          html: defaultLabel.html,
        }
      : null,
  )

  // Make sure we update the preview Label
  // when switching product
  useEffect(() => {
    if (!activeTab?.productId) return
    if (defaultLabel) {
      setLabelCustomization({
        title: defaultLabel?.rawTitle,
        subtitle: defaultLabel?.rawSubTitle,
        computeSubtitleFirst: true,
        html: defaultLabel.html,
      })
    }
  }, [activeTab?.productId])

  const [prevCustomization, setDefaultTitle] = useState(null)

  useEffect(() => {
    const currentDefaultTitle =
      activeCustomizationVariant?.defaultCustomizationTitle ??
      defaultLabel?.rawTitle ??
      null

    const isEngraving = labelCustomizationProduct?.productType === 'engraving'

    const title = isEngraving
      ? activeCustomizationVariant?.defaultCustomizationTitle ??
        defaultLabel?.rawTitle
      : defaultLabel?.rawTitle

    const shouldSwitchToDefault =
      labelCustomizationProduct?.productType !== prevCustomization
    setDefaultTitle(labelCustomizationProduct?.productType)
    if (shouldSwitchToDefault) {
      return setLabelCustomization((prev) => ({
        ...prev,
        title,
      }))
    }
    if (title?.toLowerCase() !== labelCustomization?.title?.toLowerCase()) {
      return setLabelCustomization((prev) => ({
        ...prev,
        title: labelCustomization?.title,
      }))
    }

    setLabelCustomization((prev) => ({
      ...prev,
      title,
    }))
  }, [
    activeTab?.productId,
    activeCustomizationVariant?.id,
    JSON.stringify(customizerVariants),
  ])

  useEffect(() => {
    if (activeTab?.productId) {
      setUserSelection((prev) => {
        const newState = prev
        newState[activeTabIndex][PRODUCT_TYPES.SAUCER] =
          mainCustomizationActiveVariant?.disablesSaucer
            ? null
            : newState[activeTabIndex][PRODUCT_TYPES.SAUCER]

        return newState
      })
    }
  }, [userSelection])

  const mainCustomizationProductSku =
    mainCustomizationActiveVariant?.sku ?? null

  const slicedSku =
    mainCustomizationProductSku?.slice(
      mainCustomizationProductSku.lastIndexOf('-') + 1,
    ) ?? 0

  const {
    mutate: mutatePreviewImage,
    isLoading: isLoadingLabelPreview,
    data: previewImageData,
  } = useGetPreviewImage()

  const {
    mutate,
    data: postPreviewData,
    isLoading: isLoadingLabelPreviewData,
  } = usePostPreview({
    onSuccess: (customData, variables) => {
      if (
        disablePreview ||
        variables.variant.id !== activeCustomizationVariant?.id ||
        variables.variant.sku !== activeCustomizationVariant?.sku ||
        labelCustomization.title !== variables.title
      )
        return

      let variantSku = currentVariant?.sku ?? null
      if (slicedSku) {
        variantSku += `-${slicedSku}`
      }

      mutatePreviewImage({
        variantSku: `${variantSku}`,
        cacheKey: customData?.key,
      })
    },
    onError: (error) => {
      console.log(error)
    },
  })
  const [previewData, setPreviewData] = useState(
    postPreviewData ?? defaultLabel,
  )

  useEffect(() => {
    if (isLoadingLabelPreviewData) return
    setPreviewData(postPreviewData ?? defaultLabel)
  }, [postPreviewData, defaultLabel])

  const [currentLabelInputsContent, setCurrentLabelInputsContent] = useState({
    title: previewData?.rawTitle,
    subtitle: previewData?.rawSubTitle,
    html: previewData?.html,
  })

  useEffect(() => {
    if (!activeTab?.productId) return
    setCurrentLabelInputsContent({
      title: previewData?.rawTitle,
      subtitle: previewData?.rawSubTitle,
      html: previewData?.html,
    })
  }, [userLabels, previewData, activeTab?.productId])

  // This has to be there because of particular cases in fragrances
  // To replace asap
  const disableDefaultLabel =
    !labelCustomizationVariant &&
    product?.productType === PRODUCT_TYPES.FRAGRANCE

  // if we are in a bundle and the user has not selected a label customization
  // and our product should have a default label
  // make sure that we use the default label
  const useDefault = !disableDefaultLabel && !labelCustomizationVariant
  useEffect(() => {
    setUserLabels((prev) => {
      const newState = prev
      newState[activeTabIndex] = disableDefaultLabel
        ? null
        : useDefault
        ? defaultLabel
        : previewData
      return [...newState]
    })
  }, [previewData, labelCustomizationVariant])

  /* Herbarium variants upsales */
  const [currentUpsaleId, setCurrentUpsaleId] = useState<string>(null)

  const selectedLabelCustomization = activeTab?.labelCustomization ?? false

  const isCustomized =
    Boolean(labelCustomizationVariant) &&
    (!isDisabledEngraving || !isDisabledLabelCustomization)

  const { data: previewBackgroundUrl, isLoading: isLoadingBackground } =
    useQuery(
      [
        'preview-background',
        mainCustomizationActiveVariant?.id,
        currentVariant?.id,
        labelCustomization,
      ],
      async () => {
        if (!isCustomized) return null

        const bg = await getBackgroundUrl(
          currentVariant,
          mainCustomizationActiveVariant,
          product.productType,
          labelCustomizationProduct?.productType === 'engraving',
        )

        return bg
      },
      {
        refetchOnMount: false,
        keepPreviousData: true,
      },
    )
  // saucer image
  const { data: saucerImgUrl, isLoading: isLoadingSaucerImg } = useQuery(
    ['preview-saucer', customizerSaucerActiveVariant?.id],
    async () => {
      return await getSaucerImgUrl(customizerSaucerActiveVariant)
    },
    {
      refetchOnMount: false,
      keepPreviousData: true,
    },
  )

  useEffect(() => {
    if (activeCustomizationVariant) {
      mutate({
        ...labelCustomization,
        variant: activeCustomizationVariant,
      })
    } else if (currentVariant && isCustomized) {
      mutatePreviewImage({
        variantSku: `${currentVariant?.sku}-${slicedSku}`,
        cacheKey: defaultLabel?.key,
      })
    }
  }, [labelCustomization, activeCustomizationVariant?.id])

  useEffect(() => {
    if (activeCustomizationVariant) {
      mutatePreviewImage({
        variantSku: `${currentVariant?.sku}-${slicedSku}`,
        cacheKey: postPreviewData?.key,
      })
    }
  }, [slicedSku])

  const isLoadingPreviewImages =
    isLoadingBackground || isLoadingLabelPreview || isLoadingSaucerImg

  const hasSaucer =
    customizerSaucer && customizerSaucerActiveVariant && saucerImgUrl
  const [previews, setPreviews] = useState<any[]>(data.map((tab) => {}))

  /* Here we gather the image src that will constitute the
    preview, depending on the customization state */
  const getPreviewSources = () => {
    const sources = {}
    if (disablePreview || (!isCustomized && !hasSaucer)) {
      setPreviews((prev) =>
        prev.map((tab, i) => (activeTabIndex === i ? sources : tab)),
      )
      return
    }

    if (isLoadingBackground) {
      setPreviews(previews)
      return
    }

    if (
      mainCustomizationProduct ||
      (product.productType === PRODUCT_TYPES.CANDLEV2 &&
        selectedLabelCustomization)
    ) {
      const mainImage = selectedLabelCustomization
        ? previewBackgroundUrl?.url
        : customizerVariants?.[0]?.variant?.variantImage?.src

      sources['background'] = mainImage ? { src: mainImage } : null
    }
    if (hasSaucer) {
      sources['saucer'] = saucerImgUrl ? { src: saucerImgUrl?.url } : null
    }

    if (
      labelCustomization &&
      isCustomized &&
      selectedLabelCustomization &&
      !previewBackgroundUrl?.isFallBack
    ) {
      sources['label'] = isLoadingLabelPreview
        ? previews[activeTabIndex]['label'] ?? null
        : previewImageData
        ? {
            src: previewImageData,
            type: labelCustomizationProduct?.productType,
          }
        : null
    }

    setPreviews((prev) =>
      prev.map((tab, i) => (activeTabIndex === i ? sources : tab)),
    )
  }

  useEffect(() => {
    getPreviewSources()
  }, [
    isCustomized,
    disablePreview,
    previewBackgroundUrl,
    saucerImgUrl,
    previewImageData,
    customizerVariants?.[0]?.variant?.variantImage,
  ])

  // We are updating the provider data on tab change until we fetch all tabs
  const { isLoading: isLoadingTabProducts, data: tabProduct } = useQuery(
    [
      'get_tab_products',
      JSON.stringify(`${shopifyProduct.shopifyId}-${activeTabIndex}`),
    ],
    async () => {
      return (
        (await fetchAndSerializeTabProducts(
          packTabsIds[activeTabIndex],
          data.flat(),
          locale,
        )) ?? []
      )
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: activeTabIndex >= 0 && isMultiBundle,
    },
  )

  useEffect(() => {
    if (isMultiBundle && activeTabIndex >= 0) {
      setData((prev) =>
        Array.isArray(prev)
          ? prev.map((tab, i) => (i === activeTabIndex ? tabProduct : tab))
          : null,
      )
    }
  }, [tabProduct])

  const addToCartDefaultProps = useGetAddToCartProductProps({
    userSelection,
    data,
    shopifyProduct: upToDateShopifyProduct,
    isGiftCard,
    loveNoteAttributes: loveNote?.[0],
    slug,
    atelierDate,
    userLabels,
    customizationImageState: customizationImageState[0],
    currentVariant,
    isMultiBundle,
  })

  const [addToCartProps, setCartProps] = useState(null)
  useEffect(() => {
    setCartProps(addToCartDefaultProps)
  }, [
    userSelection,
    data,
    isGiftCard,
    loveNote?.[0],
    slug,
    atelierDate,
    userLabels,
    currentUpsaleId,
    currentVariant,
    upToDateShopifyProduct,
    customizationImageState[0],
  ])

  const providerValue = useMemo(() => {
    return {
      addToCartDefaultProps: addToCartProps,
      customizerProducts,
      isDisabledEngraving,
      labelCustomization,
      isLoadingBackground,
      isLoadingLabelPreview,
      isLoadingLabelPreviewData,
      isDisabledLabelCustomization,
      previewSources: previews,
      mainCustomizationProduct,
      currentUpsaleId,
      loveNote,
      currentLabelInputsContent,
      shopifyId,
      isLoadingPreviewImages,
      isBundle,
      shopifyProduct,
      setCurrentUpsaleId,
      setAtelierDate,
      setLabelCustomization,
      isLoadingTabProducts,
      isMultiBundle,
      customizationImageState,
    }
  }, [
    upToDateShopifyProduct,
    addToCartProps,
    setLabelCustomization,
    labelCustomization,
    previews,
    isLoadingBackground,
    isLoadingLabelPreview,
    isLoadingLabelPreviewData,
    mainCustomizationProduct,
    currentUpsaleId,
    setCurrentUpsaleId,
    setAtelierDate,
    isBundle,
    loveNote,
    isLoadingTabProducts,
    customization,
    currentLabelInputsContent,
    shopifyId,
    isMultiBundle,
    isLoadingPreviewImages,
    upToDateShopifyProduct,
  ])

  return <Context.Provider value={providerValue}>{children}</Context.Provider>
}

export default ProductHeaderProvider
